import React, { useEffect, useState, useCallback } from 'react';
import './PrinterSettings.css';
import { Icon } from '../ui/icons';
import { Button, IconButton, Spinner } from '../ui/components';
import Platform from '../utils/platform';
import BluetoothPrinterService, { BtDevice } from '../services/BluetoothPrinterService';
import { getBinding, setBinding, clearBinding, markVerified, PrinterBinding } from '../utils/printerBindings';

interface Printer {
  id: string;
  name?: string;
  description?: string;
  type?: string;
  model?: string;
  connectionType?: string;
  ticketWidth?: number;
  hasAutoCutter?: boolean;
}

type TestState = { status: 'idle' | 'testing' | 'ok' | 'error'; message?: string };

const PrinterSettings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [printers, setPrinters] = useState<Printer[]>([]);
  const [error, setError] = useState<string | null>(null);

  // per-printer local binding state (deviceId → name), hydrated from localStorage
  const [bindings, setBindings] = useState<Record<string, PrinterBinding | null>>({});

  // device-picker state
  const [pickerFor, setPickerFor] = useState<string | null>(null);
  const [devices, setDevices] = useState<BtDevice[]>([]);
  const [scanning, setScanning] = useState(false);
  const [pickerError, setPickerError] = useState<string | null>(null);

  const [testState, setTestState] = useState<Record<string, TestState>>({});

  const supported = BluetoothPrinterService.isSupported();

  const loadPrinters = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/Printer`, {
        method: 'GET',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to load printers');
      const all: Printer[] = await res.json();
      const bt = all.filter((p) => (p.connectionType || '').toLowerCase() === 'bluetooth');
      setPrinters(bt);
      const b: Record<string, PrinterBinding | null> = {};
      bt.forEach((p) => { b[p.id] = getBinding(p.id); });
      setBindings(b);
    } catch (e: any) {
      setError(e?.message || 'Failed to load printers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPrinters(); }, [loadPrinters]);

  const openPicker = async (printerId: string) => {
    setPickerFor(printerId);
    setDevices([]);
    setPickerError(null);
    setScanning(true);
    try {
      const granted = await BluetoothPrinterService.ensurePermission();
      if (!granted) { setPickerError('Bluetooth permission was denied.'); return; }
      const found = await BluetoothPrinterService.listDevices();
      // likely printers first
      found.sort((a, b) => Number(b.likelyPrinter) - Number(a.likelyPrinter));
      setDevices(found);
      if (found.length === 0) {
        setPickerError(
          Platform.isAndroid()
            ? 'No paired Bluetooth devices found. Pair the printer in Android Settings first, then scan again.'
            : 'No serial/Bluetooth ports found. Pair the printer in Windows Bluetooth settings first.',
        );
      }
    } catch (e: any) {
      setPickerError(e?.message || 'Could not list devices');
    } finally {
      setScanning(false);
    }
  };

  const bindDevice = (printerId: string, device: BtDevice) => {
    const binding = { deviceId: device.id, deviceName: device.name };
    setBinding(printerId, binding);
    setBindings((prev) => ({ ...prev, [printerId]: binding }));
    setPickerFor(null);
  };

  const unbind = (printerId: string) => {
    clearBinding(printerId);
    setBindings((prev) => ({ ...prev, [printerId]: null }));
    setTestState((prev) => ({ ...prev, [printerId]: { status: 'idle' } }));
  };

  const runTest = async (printer: Printer) => {
    const binding = bindings[printer.id];
    if (!binding) return;
    setTestState((prev) => ({ ...prev, [printer.id]: { status: 'testing' } }));
    const res = await BluetoothPrinterService.testPrint(printer, binding.deviceId);
    if (res.success) {
      const updated = markVerified(printer.id);
      setBindings((prev) => ({ ...prev, [printer.id]: updated }));
    }
    setTestState((prev) => ({
      ...prev,
      [printer.id]: res.success
        ? { status: 'ok', message: 'Test slip sent — printer responded' }
        : { status: 'error', message: res.error || 'Test failed' },
    }));
  };

  return (
    <div className="printer-settings ps2 ps-scope">
      <header className="ps2-head">
        <div className="ps2-head__lead">
          <h1 className="ps2-title"><Icon.Printer size={22} /> Printer Settings</h1>
          <span className="ps2-sub">Connect this device to your Bluetooth printers</span>
        </div>
        <button className="ps2-tool" onClick={loadPrinters}>
          <Icon.Refresh size={15} /> Refresh
        </button>
      </header>

      {!supported && (
        <div className="ps2-banner ps2-banner--warn">
          <Icon.TriangleAlert size={16} />
          <span>
            Bluetooth printing is available in the <strong>tablet app</strong> and the{' '}
            <strong>desktop app</strong>. You're currently on the web version, so devices can't be paired here.
          </span>
        </div>
      )}

      <div className="ps2-info">
        Bluetooth printers are configured in <strong>pos.tableview.com</strong>, then paired to each device here.
        This binding is saved on <strong>this {Platform.isElectron() ? 'computer' : 'device'} only</strong>.
      </div>

      {loading ? (
        <div className="ps2-empty"><Spinner /> Loading printers…</div>
      ) : error ? (
        <div className="ps2-banner ps2-banner--error"><Icon.CircleAlert size={16} /> {error}</div>
      ) : printers.length === 0 ? (
        <div className="ps2-empty">
          <Icon.Printer size={34} />
          <h3>No Bluetooth printers configured</h3>
          <p>Add a printer with connection type “Bluetooth” in pos.tableview.com, then refresh.</p>
        </div>
      ) : (
        <div className="ps2-list">
          {printers.map((printer) => {
            const binding = bindings[printer.id];
            const test = testState[printer.id] || { status: 'idle' };
            const isPicking = pickerFor === printer.id;
            return (
              <div key={printer.id} className="ps2-card">
                <div className="ps2-card__main">
                  <div className="ps2-card__icon"><Icon.Printer size={20} /></div>
                  <div className="ps2-card__body">
                    <div className="ps2-card__name">{printer.name || 'Unnamed printer'}</div>
                    <div className="ps2-card__meta">
                      {[printer.type, printer.model].filter(Boolean).join(' · ') || 'Bluetooth'}
                    </div>
                    {!binding ? (
                      <div className="ps2-bind ps2-bind--none">
                        <Icon.Plug size={14} /> Not set up on this device
                      </div>
                    ) : binding.verifiedAt ? (
                      <div className="ps2-bind ps2-bind--ok">
                        <Icon.CircleCheck size={14} /> Connected to <strong>{binding.deviceName}</strong> · verified
                      </div>
                    ) : (
                      <div className="ps2-bind ps2-bind--pending">
                        <Icon.Link size={14} /> Assigned to <strong>{binding.deviceName}</strong>
                        <span className="ps2-bind__hint"> — run a test print to verify</span>
                      </div>
                    )}
                    {test.status === 'ok' && <div className="ps2-test ps2-test--ok"><Icon.Check size={13} /> {test.message}</div>}
                    {test.status === 'error' && <div className="ps2-test ps2-test--err"><Icon.TriangleAlert size={13} /> {test.message}</div>}
                  </div>
                </div>

                <div className="ps2-card__actions">
                  <Button variant="secondary" size="sm" onClick={() => openPicker(printer.id)} disabled={!supported}>
                    <Icon.Link size={15} /> {binding ? 'Change device' : 'Select device'}
                  </Button>
                  {binding && (
                    <>
                      <Button variant="primary" size="sm" onClick={() => runTest(printer)} disabled={test.status === 'testing'}>
                        {test.status === 'testing' ? <Spinner size={14} /> : <Icon.Printer size={15} />} Test print
                      </Button>
                      <IconButton variant="ghost" size="icon-sm" aria-label="Unbind" onClick={() => unbind(printer.id)}>
                        <Icon.Trash size={15} />
                      </IconButton>
                    </>
                  )}
                </div>

                {isPicking && (
                  <div className="ps2-picker">
                    <div className="ps2-picker__head">
                      <span>Select a paired device</span>
                      <div className="ps2-picker__headtools">
                        <button className="ps2-tool ps2-tool--xs" onClick={() => openPicker(printer.id)} disabled={scanning}>
                          <Icon.Refresh size={13} /> Rescan
                        </button>
                        <IconButton variant="ghost" size="icon-sm" aria-label="Close" onClick={() => setPickerFor(null)}>
                          <Icon.ChevronUp size={16} />
                        </IconButton>
                      </div>
                    </div>
                    {scanning ? (
                      <div className="ps2-picker__state"><Spinner size={16} /> Scanning…</div>
                    ) : pickerError ? (
                      <div className="ps2-picker__state ps2-picker__state--err">{pickerError}</div>
                    ) : (
                      <ul className="ps2-devices">
                        {devices.map((d) => (
                          <li key={d.id}>
                            <button className="ps2-device" onClick={() => bindDevice(printer.id, d)}>
                              <span className="ps2-device__name">
                                {d.likelyPrinter && <span className="ps2-device__badge">Printer</span>}
                                {d.name}
                              </span>
                              <span className="ps2-device__id">{d.id}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PrinterSettings;
